import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { 
    AcademicCapIcon, 
    UsersIcon, 
    GlobeAltIcon,
    RocketLaunchIcon,
    LightBulbIcon,
    HeartIcon
} from '@heroicons/react/24/outline';

const AboutPage = () => {
    const values = [
        {
            icon: AcademicCapIcon,
            title: 'Education First',
            description: 'We believe quality aviation and aerospace education should be accessible to everyone, everywhere.'
        },
        {
            icon: LightBulbIcon,
            title: 'Innovation',
            description: 'Combining cutting-edge technology with proven teaching methods to create engaging learning experiences.'
        },
        {
            icon: UsersIcon,
            title: 'Community',
            description: 'Building a global community of aviation enthusiasts, students, and professionals.'
        },
        {
            icon: GlobeAltIcon,
            title: 'Global Reach',
            description: 'Breaking down geographical barriers to make aviation knowledge available worldwide.'
        }
    ];

    const stats = [
        { value: '1000+', label: 'Active Learners' },
        { value: '50+', label: 'Expert Courses' },
        { value: '100+', label: 'Research Articles' },
        { value: '24/7', label: 'Learning Access' }
    ];

    return (
        <>
            <SEO 
                title="About Us - AircoreDynamics"
                description="Learn about AircoreDynamics, our mission to democratize aviation education, and our commitment to providing world-class aerospace learning experiences."
                keywords="about aircoredynamics, aviation education mission, aerospace learning platform, aviation courses online"
                image="https://aircoredynamics.com/og-image.png"
                url="/about"
                type="website"
            />
            
            <div className="max-w-7xl mx-auto">
                {/* Hero Section */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                        About{' '}
                        <span className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                            AircoreDynamics
                        </span>
                    </h1>
                    <p className="text-xl text-light-text/80 dark:text-dark-text/80 max-w-3xl mx-auto leading-relaxed">
                        Democratizing aviation and aerospace education through innovative online learning experiences
                    </p>
                </motion.div>

                {/* Mission Statement */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="card p-8 md:p-12 mb-12 bg-gradient-to-br from-light-primary/5 to-blue-500/5 dark:from-dark-primary/5 dark:to-cyan-400/5"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <RocketLaunchIcon className="h-10 w-10 text-light-primary dark:text-dark-primary" />
                        <h2 className="text-3xl font-bold">Our Mission</h2>
                    </div>
                    <p className="text-lg leading-relaxed mb-4">
                        At AircoreDynamics, we're on a mission to make aviation and aerospace education accessible to everyone, 
                        regardless of their location or background. We believe that the future of flight depends on nurturing 
                        the next generation of aviation professionals, engineers, and enthusiasts.
                    </p>
                    <p className="text-lg leading-relaxed">
                        Through comprehensive online courses, in-depth research articles, and up-to-date industry news, 
                        we're building a global community of learners passionate about aviation, aerodynamics, and space exploration.
                    </p>
                </motion.div>

                {/* Stats Grid */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
                >
                    {stats.map((stat, index) => (
                        <div key={index} className="card p-6 text-center">
                            <div className="text-3xl md:text-4xl font-bold text-light-primary dark:text-dark-primary mb-2">
                                {stat.value}
                            </div>
                            <div className="text-sm text-light-text/70 dark:text-dark-text/70">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </motion.div>

                {/* Our Values */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-12"
                >
                    <h2 className="text-3xl font-bold text-center mb-8">Our Core Values</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                                className="card p-6 hover:shadow-xl transition-shadow"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10">
                                        <value.icon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                                        <p className="text-light-text/70 dark:text-dark-text/70">
                                            {value.description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* What We Offer */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="card p-8 md:p-12 mb-12"
                >
                    <h2 className="text-3xl font-bold mb-8 text-center">What We Offer</h2>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                                <AcademicCapIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                                Professional Courses
                            </h3>
                            <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                                Comprehensive courses covering aerodynamics, flight mechanics, aircraft systems, and more. 
                                Each course includes interactive quizzes and official certificates upon completion.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                                <LightBulbIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                                Research & Articles
                            </h3>
                            <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                                In-depth research articles, technical analyses, and educational content written by 
                                aviation experts and industry professionals.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                                <GlobeAltIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                                Latest News
                            </h3>
                            <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                                Stay updated with breaking news in aviation, aerospace technology, space exploration, 
                                and the latest developments in flight technology.
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="card p-8 md:p-12 text-center bg-gradient-to-br from-light-primary/10 to-blue-500/10 dark:from-dark-primary/10 dark:to-cyan-400/10"
                >
                    <HeartIcon className="h-12 w-12 text-light-primary dark:text-dark-primary mx-auto mb-4" />
                    <h2 className="text-3xl font-bold mb-4">Join Our Community</h2>
                    <p className="text-lg text-light-text/80 dark:text-dark-text/80 mb-6 max-w-2xl mx-auto">
                        Whether you're a student, professional, or aviation enthusiast, there's a place for you at AircoreDynamics. 
                        Start your learning journey today and be part of the future of aviation.
                    </p>
                    <a href="/register" className="btn-primary inline-block">
                        Get Started Free
                    </a>
                </motion.div>
            </div>
        </>
    );
};

export default AboutPage;
